/**
 * Minecraft-style Web Audio synth. Zero binary assets, CC0 by design.
 * Every "sound" is a short envelope on a square/triangle wave tuned to feel Minecraft-y.
 */

const STORAGE_KEY = "lostdc:sound:v1";

const defaults = { enabled: true, volume: 0.6 };

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaults };
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return { ...defaults };
  }
}

function savePrefs(p) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch (err) { console.debug("sound prefs persist failed", err?.message); }
}

let prefs = loadPrefs();
let ctx = null;
const listeners = new Set();

function getCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch { return null; }
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

function tone({ freq = 440, freq2 = null, type = "square", dur = 0.12, gain = 0.5, delay = 0 }) {
  const c = getCtx();
  if (!c || !prefs.enabled) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (freq2 != null) osc.frequency.exponentialRampToValueAtTime(Math.max(1, freq2), t0 + dur);
  const peak = Math.max(0.0001, gain * prefs.volume);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

export const sfx = {
  // ui.button.click — short low pop
  click: () => tone({ freq: 520, freq2: 360, type: "square", dur: 0.08, gain: 0.25 }),
  // hover — very subtle
  hover: () => tone({ freq: 620, type: "triangle", dur: 0.04, gain: 0.08 }),
  // note.pling — bright pling
  pling: () => {
    tone({ freq: 880, type: "triangle", dur: 0.18, gain: 0.35 });
    tone({ freq: 1320, type: "triangle", dur: 0.22, gain: 0.22, delay: 0.02 });
  },
  // random.levelup — 3 rising notes
  levelup: () => {
    tone({ freq: 660, type: "triangle", dur: 0.12, gain: 0.35 });
    tone({ freq: 880, type: "triangle", dur: 0.14, gain: 0.35, delay: 0.10 });
    tone({ freq: 1320, type: "triangle", dur: 0.20, gain: 0.35, delay: 0.22 });
  },
  // villager.no — descending buzz
  no: () => {
    tone({ freq: 280, freq2: 180, type: "sawtooth", dur: 0.22, gain: 0.35 });
  },
  // copy — dry blip
  copy: () => tone({ freq: 780, freq2: 940, type: "square", dur: 0.09, gain: 0.3 }),
  // success (like a small levelup)
  success: () => {
    tone({ freq: 800, type: "triangle", dur: 0.10, gain: 0.32 });
    tone({ freq: 1100, type: "triangle", dur: 0.14, gain: 0.32, delay: 0.09 });
  },
  // error
  error: () => sfx.no(),
  // menu open — soft chord
  menu: () => {
    tone({ freq: 440, type: "triangle", dur: 0.14, gain: 0.22 });
    tone({ freq: 660, type: "triangle", dur: 0.14, gain: 0.18, delay: 0.02 });
  },
  // -- Notification-type distinct chimes --
  chime_message: () => {
    tone({ freq: 900, type: "sine", dur: 0.12, gain: 0.30 });
    tone({ freq: 1200, type: "sine", dur: 0.14, gain: 0.24, delay: 0.05 });
  },
  chime_mention: () => {
    tone({ freq: 660, type: "triangle", dur: 0.10, gain: 0.30 });
    tone({ freq: 990, type: "triangle", dur: 0.12, gain: 0.30, delay: 0.07 });
    tone({ freq: 1320, type: "triangle", dur: 0.16, gain: 0.28, delay: 0.16 });
  },
  chime_follow: () => {
    tone({ freq: 700, type: "sine", dur: 0.10, gain: 0.28 });
    tone({ freq: 950, type: "sine", dur: 0.14, gain: 0.24, delay: 0.10 });
  },
  chime_community: () => {
    tone({ freq: 500, type: "triangle", dur: 0.12, gain: 0.24 });
    tone({ freq: 750, type: "triangle", dur: 0.14, gain: 0.22, delay: 0.08 });
  },
  chime_event: () => {
    tone({ freq: 1100, type: "square", dur: 0.09, gain: 0.22 });
    tone({ freq: 1400, type: "square", dur: 0.09, gain: 0.20, delay: 0.09 });
    tone({ freq: 1650, type: "square", dur: 0.12, gain: 0.20, delay: 0.18 });
  },
  chime_approved: () => {
    tone({ freq: 660, type: "triangle", dur: 0.10, gain: 0.32 });
    tone({ freq: 880, type: "triangle", dur: 0.12, gain: 0.32, delay: 0.08 });
    tone({ freq: 1320, type: "triangle", dur: 0.20, gain: 0.30, delay: 0.18 });
  },
  chime_creator: () => {
    tone({ freq: 520, type: "sawtooth", dur: 0.10, gain: 0.24 });
    tone({ freq: 780, type: "sawtooth", dur: 0.14, gain: 0.22, delay: 0.10 });
    tone({ freq: 1040, type: "sawtooth", dur: 0.16, gain: 0.20, delay: 0.22 });
  },
  chime_announcement: () => {
    tone({ freq: 400, freq2: 300, type: "sawtooth", dur: 0.20, gain: 0.32 });
    tone({ freq: 600, type: "square", dur: 0.14, gain: 0.24, delay: 0.20 });
  },
};

export function getPrefs() { return { ...prefs }; }

export function setPrefs(next) {
  prefs = { ...prefs, ...next };
  savePrefs(prefs);
  listeners.forEach((l) => l(prefs));
}

export function subscribePrefs(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Helper: attach click sound to any button globally (opt-in via data-sound attr)
export function playByName(name) {
  const fn = sfx[name];
  if (fn) fn();
}
